

import { defaultMemoryCacheStore } from './MemoryCacheStore';

/**
 * In-memory cache-aside for the Campaigns UI (mirrors API pattern, browser-local TTL).
 *
 * Why separate from API Redis: avoids duplicate list XHR when switching tabs within the same
 * session. Keys are tenant-scoped in campaignListCachePolicy.js. Cleared via
 * clearClientSessionCaches() on logout, account switch, and explicit mutations.
 *
 * SOLID: depends on ICacheStore only; swap store in tests via constructor injection.
 */
export class CacheAsideExecutor {
  /**
   * @param {{ store?: import('./ICacheStore').ICacheStore }} [options]
   */
  constructor({ store = defaultMemoryCacheStore } = {}) {
    this.store = store;
  }

  /**
   * @template T
   * @param {object} options
   * @param {string} options.key
   * @param {number} options.ttlMs
   * @param {() => Promise<T>} options.loader
   * @param {(T) => boolean} [options.shouldCache]
   * @returns {Promise<T>}
   */
  async run({ key, ttlMs, loader, shouldCache }) {
    const cached = await this.store.get(key);
    if (cached !== null && cached !== undefined) {
      return cached;
    }

    const value = await loader();
    const allowCache = shouldCache ? shouldCache(value) : value != null;
    if (allowCache) {
      await this.store.set(key, value, ttlMs);
    }
    return value;
  }

  async invalidate(key) {
    if (this.store.delete) {
      await this.store.delete(key);
    }
  }

  clear() {
    if (typeof this.store.clear === 'function') {
      this.store.clear();
    }
  }
}

export const defaultCacheAsideExecutor = new CacheAsideExecutor();
